import React, { useState, useEffect } from 'react'
import { FiPlus, FiSearch, FiEdit, FiTrash2, FiEye } from 'react-icons/fi'
import { FaFilter } from 'react-icons/fa'
import {
  stockService,
  StockUniverse,
  StockUniverseListResponse,
} from '../services/stockService'
import MainHeader from '../components/MainHeader'
import ConfirmationModal from '../common/ConfirmationModal'
import { motion } from 'framer-motion'

interface Stock {
  id: string
  symbol: string
  company_name: string
  sector?: string
  exchange: string
}

interface StockUniverseManagementProps {
  onBackToDashboard: () => void
}

const StockUniverseManagement: React.FC<StockUniverseManagementProps> = ({
  onBackToDashboard,
}) => {
  const [universes, setUniverses] = useState<StockUniverse[]>([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalUniverses, setTotalUniverses] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [activeFilter, setActiveFilter] = useState<boolean | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedUniverse, setSelectedUniverse] =
    useState<StockUniverse | null>(null)
  const [availableStocks, setAvailableStocks] = useState<Stock[]>([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [universeToDelete, setUniverseToDelete] =
    useState<StockUniverse | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [loadingItems, setLoadingItems] = useState<Set<string>>(new Set())

  const fetchUniverses = async () => {
    setLoading(true)
    try {
      const response: StockUniverseListResponse =
        await stockService.getStockUniverses(
          currentPage,
          10,
          searchTerm,
          activeFilter === null ? undefined : activeFilter
        )
      setUniverses(response.universes)
      setTotalPages(response.total_pages)
      setTotalUniverses(response.total)
    } catch (error) {
      console.error('Error fetching stock universes:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchAvailableStocks = async () => {
    try {
      const response = await stockService.getStocks(
        1,
        200,
        undefined,
        undefined,
        undefined,
        true
      )
      setAvailableStocks(response.stocks)
    } catch (error) {
      console.error('Error fetching available stocks:', error)
    }
  }

  useEffect(() => {
    fetchUniverses()
    fetchAvailableStocks()
  }, [currentPage, searchTerm, activeFilter])

  const handleCreateUniverse = async (universeData: Partial<StockUniverse>) => {
    try {
      await stockService.createStockUniverse(universeData)
      setShowCreateModal(false)
      fetchUniverses()
    } catch (error) {
      console.error('Error creating stock universe:', error)
    }
  }

  const handleUpdateUniverse = async (
    universeId: string,
    universeData: Partial<StockUniverse>
  ) => {
    try {
      await stockService.updateStockUniverse(universeId, universeData)
      setShowEditModal(false)
      setSelectedUniverse(null)
      fetchUniverses()
    } catch (error) {
      console.error('Error updating stock universe:', error)
    }
  }

  const handleDeleteUniverse = (universe: StockUniverse) => {
    setUniverseToDelete(universe)
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    if (!universeToDelete) return

    try {
      setDeleteLoading(true)
      await stockService.deleteStockUniverse(universeToDelete.id)
      setShowDeleteModal(false)
      setUniverseToDelete(null)
      fetchUniverses()
    } catch (error) {
      console.error('Error deleting stock universe:', error)
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setUniverseToDelete(null)
  }

  // const handleEditUniverse = (universe: StockUniverse) => {
  //   setSelectedUniverse(universe)
  //   setShowEditModal(true)
  // }

  const [formData, setFormData] = useState<Partial<StockUniverse>>({})

  const handleEditUniverse = async (universeId: any) => {
    try {
      const stockData = await stockService.getStockById(universeId)
      setSelectedUniverse(stockData)
      setShowEditModal(true)
    } catch (error) {
      console.error('Error fetching stock:', error)
    }
  }

  const clearFilters = () => {
    setSearchTerm('')
    setActiveFilter(null)
  }

  return (
    <div>
      <MainHeader onBackToDashboard={onBackToDashboard || (() => {})} />
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6 ">
          <div>
            <motion.button
              onClick={onBackToDashboard}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors duration-200"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}>
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Dashboard
            </motion.button>
            <motion.h1
              className="text-3xl font-bold text-gray-900"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}>
              Stock Universe Management
            </motion.h1>

            <motion.p
              className="mt-2 text-sm text-gray-600"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}>
              Manage stock universes
            </motion.p>
          </div>
          <motion.button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
            <FiPlus />
            Create Universe
          </motion.button>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search universes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <select
              value={activeFilter === null ? '' : activeFilter.toString()}
              onChange={(e) =>
                setActiveFilter(
                  e.target.value === '' ? null : e.target.value === 'true'
                )
              }
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>

            {(searchTerm || activeFilter !== null) && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-600 hover:text-gray-800">
                Clear All
              </button>
            )}
          </div>
        </div>

        {/* Universes Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading universes...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stocks Count
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {universes.map((universe) => (
                      <tr key={universe.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium text-gray-900">
                            {universe.name}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {universe.description || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {universe.stock_count || 0} stocks
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                universe.is_active
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                              {universe.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <button
                              onClick={() =>
                                handleUpdateUniverse(universe.id, {
                                  is_active: !universe.is_active,
                                })
                              }
                              disabled={loadingItems.has(universe.id)}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 ${
                                universe.is_active
                                  ? 'bg-blue-600'
                                  : 'bg-gray-200'
                              }`}>
                              {loadingItems.has(universe.id) ? (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
                                </div>
                              ) : (
                                <span
                                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                    universe.is_active
                                      ? 'translate-x-5'
                                      : 'translate-x-1'
                                  }`}
                                />
                              )}
                            </button>
                          </div>
                          {/* <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              universe.is_active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {universe.is_active ? "Active" : "Inactive"}
                          </span>
 */}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(universe.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditUniverse(universe?.id)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit">
                              <FiEdit />
                            </button>
                            <button
                              onClick={() => handleDeleteUniverse(universe)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete">
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
                    Previous
                  </button>
                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">
                        {(currentPage - 1) * 10 + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium">
                        {Math.min(currentPage * 10, totalUniverses)}
                      </span>{' '}
                      of <span className="font-medium">{totalUniverses}</span>{' '}
                      results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() =>
                          setCurrentPage(Math.max(1, currentPage - 1))
                        }
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">
                        Previous
                      </button>
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          const page =
                            Math.max(
                              1,
                              Math.min(totalPages - 4, currentPage - 2)
                            ) + i
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === currentPage
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}>
                              {page}
                            </button>
                          )
                        }
                      )}
                      <button
                        onClick={() =>
                          setCurrentPage(Math.min(totalPages, currentPage + 1))
                        }
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create Universe Modal */}
      {showCreateModal && (
        <UniverseModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateUniverse}
          availableStocks={availableStocks}
          mode="create"
        />
      )}

      {/* Edit Universe Modal */}
      {showEditModal && selectedUniverse && (
        <UniverseModal
          onClose={() => {
            setShowEditModal(false)
            setSelectedUniverse(null)
          }}
          onSubmit={(data) => handleUpdateUniverse(selectedUniverse.id, data)}
          availableStocks={availableStocks}
          universe={selectedUniverse}
          mode="edit"
        />
      )}

      {/* Delete Stock Universe Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Delete Stock Universe"
        message={`Are you sure you want to delete the stock universe "${universeToDelete?.name}"?`}
        confirmText="Delete"
        confirmButtonColor="bg-red-600 hover:bg-red-700"
        loading={deleteLoading}
      />
    </div>
  )
}

// Universe Modal Component
interface UniverseModalProps {
  onClose: () => void
  onSubmit: (universeData: Partial<StockUniverse>) => void
  availableStocks: Stock[]
  universe?: StockUniverse
  mode: 'create' | 'edit'
}

const UniverseModal: React.FC<UniverseModalProps> = ({
  onClose,
  onSubmit,
  availableStocks,
  universe,
  mode,
}) => {
  const [formData, setFormData] = useState({
    name: universe?.name || '',
    description: universe?.description || '',
    stock_ids: universe?.stock_ids || [],
    is_active: universe?.is_active ?? true,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  const handleStockToggle = (stockId: string) => {
    setFormData((prev) => ({
      ...prev,
      stock_ids: prev.stock_ids.includes(stockId)
        ? prev.stock_ids.filter((id) => id !== stockId)
        : [...prev.stock_ids, stockId],
    }))
  }

  const selectedStocks = availableStocks.filter((stock) =>
    formData.stock_ids.includes(stock.id)
  )

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {mode === 'create'
              ? 'Create New Stock Universe'
              : 'Edit Stock Universe'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">Active</label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Stocks ({selectedStocks.length} selected)
              </label>
              <div className="border border-gray-300 rounded-md p-4 max-h-64 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {availableStocks.map((stock) => (
                    <label
                      key={stock.id}
                      className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.stock_ids.includes(stock.id)}
                        onChange={() => handleStockToggle(stock.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-900">
                        {stock.symbol} - {stock.company_name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200">
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700">
                {mode === 'create' ? 'Create' : 'Update'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default StockUniverseManagement
